import { PieChart, X } from 'lucide-react';
import React, { useEffect, useRef } from 'react';

import type { SectorHolding } from '../types';
import { formatNum } from '../utils/formatters';

interface Props {
    ticker: string;
    holdings: SectorHolding[] | null | undefined;
    onClose: () => void;
}

// A lightweight, self-contained popover that lists an ETF's top constituents.
// Data is passed in directly from the valuation row (the /sector_valuation
// payload already embeds top_holdings), so opening it costs zero extra network
// requests. Closes on outside-click or Escape.
export const EtfHoldingsPopover: React.FC<Props> = ({ ticker, holdings, onClose }) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        const onClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('keydown', onKey);
        // Defer the click listener so the opening click doesn't immediately close it.
        const t = setTimeout(() => document.addEventListener('mousedown', onClick), 0);
        return () => {
            document.removeEventListener('keydown', onKey);
            document.removeEventListener('mousedown', onClick);
            clearTimeout(t);
        };
    }, [onClose]);

    const rows = holdings ?? [];
    const maxWeight = Math.max(...rows.map((h) => h.weightPercentage ?? 0), 1);

    return (
        // Fixed full-screen scrim keeps the popover centred and above the table.
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4">
            <div
                ref={ref}
                className="w-full max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-2.5">
                        <div className="p-1.5 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 rounded-lg">
                            <PieChart className="w-4 h-4" />
                        </div>
                        <div>
                            <h3 className="font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                                {ticker} · Top Holdings
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Largest constituents by portfolio weight
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-4 max-h-[420px] overflow-y-auto">
                    {rows.length === 0 ? (
                        <div className="py-10 text-center text-sm text-slate-400">
                            No holdings data available for {ticker}.
                        </div>
                    ) : (
                        <ul className="space-y-2">
                            {rows.map((h, i) => {
                                const weight = h.weightPercentage ?? 0;
                                const pct = (weight / maxWeight) * 100;
                                return (
                                    <li key={`${h.asset ?? i}`} className="relative">
                                        {/* Weight bar background */}
                                        <div
                                            className="absolute inset-y-0 left-0 bg-indigo-50 dark:bg-indigo-900/20 rounded-md"
                                            style={{ width: `${pct}%` }}
                                        />
                                        <div className="relative flex items-center justify-between px-3 py-2">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="font-mono font-bold text-xs text-slate-400 w-5 shrink-0">
                                                    {i + 1}
                                                </span>
                                                {h.asset ? (
                                                    <a
                                                        href={`/dashboard/stock-info-page?symbol=${encodeURIComponent(h.asset)}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="font-bold text-sm text-slate-900 dark:text-slate-100 shrink-0 hover:text-indigo-600 dark:hover:text-indigo-400 hover:underline transition-colors"
                                                        title={`Open ${h.asset} in a new window`}
                                                    >
                                                        {h.asset}
                                                    </a>
                                                ) : (
                                                    <span className="font-bold text-sm text-slate-900 dark:text-slate-100 shrink-0">
                                                        --
                                                    </span>
                                                )}
                                                <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                                    {h.name}
                                                </span>
                                            </div>
                                            <span className="font-mono font-bold text-sm text-indigo-600 dark:text-indigo-400 shrink-0 ml-2">
                                                {formatNum(weight)}%
                                            </span>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EtfHoldingsPopover;
